import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { Badge } from "@/components/ui/badge";
import { Link2, Layers } from "lucide-react";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { buildPppcPolicyActions } from "@/components/actions";
import { AppIcon } from "@/components/applications/AppIcon";
import { InteractiveTitle } from "@/components/ui/interactive-title";
import { PppcPolicySnapshot, PppcServiceKey } from "@/types/pppc";

interface PppcPolicyCardProps {
  policy: PppcPolicySnapshot;
  linkedToCatalog: boolean;
  iconUrl?: string | null;
  onView: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

const formatDate = (iso?: string) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return "—";
  }
};

export const PppcPolicyCard = ({
  policy,
  linkedToCatalog,
  iconUrl,
  onView,
  onEdit,
  onDuplicate,
  onDelete,
}: PppcPolicyCardProps) => {
  const keys = Object.keys(policy.choices) as PppcServiceKey[];
  const allow = keys.filter((k) => policy.choices[k] === "allow").length;
  const deny = keys.filter((k) => policy.choices[k] === "deny").length;

  return (
    <GlassCard
      data-testid={`pppc-card-${policy.policyId}`}
      data-policy-id={policy.policyId}
      className="hover:border-primary/40 transition-colors"
    >
      <GlassCardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 min-w-0">
            <AppIcon
              iconSrc={iconUrl ?? null}
              appName={policy.appName || policy.policyName || "PPPC Policy"}
              className="w-8 h-8 mt-0.5 flex-shrink-0"
            />
            <div className="min-w-0">
              <GlassCardTitle as="h2" className="text-base">
                <InteractiveTitle onClick={onView} className="block w-full text-base font-semibold">
                  {policy.appName || policy.policyName || "PPPC Policy"}
                </InteractiveTitle>
              </GlassCardTitle>
              <p className="text-xs font-mono text-muted-foreground truncate mt-0.5">
                {policy.identifier || "—"}
              </p>
            </div>
          </div>
          <ActionsDropdown
            actions={buildPppcPolicyActions({
              surface: "list",
              onView,
              onEdit,
              onDuplicate,
              onDelete,
            })}
          />
        </div>
      </GlassCardHeader>

      <GlassCardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline" className="text-xs">
            {policy.identifierType === "path" ? "Path" : "Bundle ID"}
          </Badge>
          {linkedToCatalog ? (
            <Badge variant="secondary" className="text-xs gap-1">
              <Link2 className="h-3 w-3" /> Linked to catalog
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs">External app</Badge>
          )}
          <Badge variant="outline" className="text-xs gap-1">
            <Layers className="h-3 w-3" />
            {keys.length} service{keys.length !== 1 ? "s" : ""}
          </Badge>
        </div>

        <div className="flex items-center gap-2 text-xs">
          {allow > 0 && (
            <Badge variant="outline" className="border-success text-success">
              {allow} Allow
            </Badge>
          )}
          {deny > 0 && (
            <Badge variant="outline" className="border-destructive text-destructive">
              {deny} Deny
            </Badge>
          )}
          {keys.length === 0 && (
            <span className="text-muted-foreground">No services configured</span>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          Updated {formatDate(policy.updatedAt)}
        </p>
      </GlassCardContent>
    </GlassCard>
  );
};
